import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, ArrowRight, MapPin, Inbox, Star, Radar, Bot, Activity,
  Building2, Sparkles, AlertTriangle, CheckCircle2, TrendingUp,
} from "lucide-react";
import { dashboardSummary } from "@/lib/api";
import { toast } from "sonner";
import WeeklyDigestCard from "@/components/WeeklyDigestCard";

/**
 * Executive Dashboard — /dashboard
 * One aggregated view of every Phase 1 signal for the current tenant.
 */

const scoreTone = (v) => (v == null ? "slate" : v >= 75 ? "emerald" : v >= 50 ? "amber" : "rose");
const TONE = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber:   "border-amber-200 bg-amber-50 text-amber-900",
  rose:    "border-rose-200 bg-rose-50 text-rose-800",
  slate:   "border-slate-200 bg-slate-50 text-slate-600",
};

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [params] = useSearchParams();
  const justOnboarded = params.get("onboarded") === "1";

  useEffect(() => {
    document.title = "Executive Dashboard — WebsiteTech.pro";
    dashboardSummary()
      .then(setData)
      .catch(() => setErr("Sign in to view your dashboard."));
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <section className="mx-auto max-w-4xl px-4 py-16">
          <Card className="border-amber-200 bg-amber-50 p-8 text-sm text-amber-900" data-testid="dashboard-err">
            {err} <Link to="/login" className="ml-2 font-semibold underline">Sign in</Link>
          </Card>
        </section>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard…
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const identity = data.identity || {};
  const lv = data.local_visibility || {};
  const inbox = data.inbox || { pipeline: {} };
  const rep = data.reputation || {};
  const si = data.search_intelligence;
  const go = data.growth_operator || {};
  const lvTone = scoreTone(lv.score);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="dashboard-page">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Activity className="h-4 w-4" /> Executive Dashboard
          </div>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">
                {identity.legal_name || "Welcome"}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                {identity.primary_category && <Badge variant="outline">{identity.primary_category}</Badge>}
                <span>{identity.location_count || 0} location{identity.location_count === 1 ? "" : "s"}</span>
                <span>· verification: <b>{identity.verification_status}</b></span>
                <span>· {data.activity?.agent_actions_30d || 0} agent actions (30d)</span>
              </div>
            </div>
            <Button asChild variant="outline" data-testid="dashboard-edit-identity">
              <Link to="/admin/identity"><Building2 className="mr-1 h-4 w-4" /> Edit identity</Link>
            </Button>
          </div>

          {justOnboarded && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="dashboard-onboarded-banner">
              <CheckCircle2 className="mr-1 inline h-4 w-4" />
              Onboarding complete. Your Local Visibility audit ran in the background — see the score below.
            </div>
          )}
          {!identity.legal_name && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mr-1 inline h-4 w-4" />
              No business identity captured yet.{" "}
              <Link to="/onboarding" className="font-semibold underline">Run the 5-step onboarding</Link>{" "}
              to unlock every module.
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8">
        {/* Top row — key scores */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Local Visibility */}
          <Card className={`border p-5 ${TONE[lvTone]}`} data-testid="dashboard-tile-lv">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest opacity-70">
                <MapPin className="h-4 w-4" /> Local Visibility
              </div>
              <Link to="/local-visibility" className="text-xs font-semibold underline">Open</Link>
            </div>
            <div className="mt-3 font-heading text-5xl font-black tabular-nums">
              {lv.score ?? "—"}
              <span className="text-lg opacity-60">/100</span>
            </div>
            <div className="mt-2 text-sm">
              {lv.estimated_missed_leads_per_month != null
                ? <>Est. <b>{lv.estimated_missed_leads_per_month}</b> leads/mo missed</>
                : "Run your first audit to see missed leads"}
            </div>
          </Card>

          {/* Inbox */}
          <Card className="border border-slate-200 bg-white p-5" data-testid="dashboard-tile-inbox">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <Inbox className="h-4 w-4" /> Unified Inbox
              </div>
              <Link to="/inbox" className="text-xs font-semibold text-orange-600 underline">Open</Link>
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-heading text-5xl font-black tabular-nums text-slate-900">{inbox.unread ?? 0}</span>
              <span className="text-sm text-slate-500">unread</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1 text-xs text-slate-600">
              {["new", "qualifying", "quoted", "won", "lost"].map((s) => (
                <span key={s} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5">
                  {s} <b>{inbox.pipeline?.[s] || 0}</b>
                </span>
              ))}
            </div>
          </Card>

          {/* Reputation */}
          <Card className="border border-slate-200 bg-white p-5" data-testid="dashboard-tile-rep">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <Star className="h-4 w-4" /> Reputation
              </div>
              <Link to="/reputation" className="text-xs font-semibold text-orange-600 underline">Open</Link>
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-heading text-5xl font-black tabular-nums text-slate-900">
                {rep.avg_rating ?? "—"}
              </span>
              <span className="text-sm text-slate-500">avg rating</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1 text-xs">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700">
                total <b>{rep.total || 0}</b>
              </span>
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-800">
                published <b>{rep.published_testimonials || 0}</b>
              </span>
              {rep.urgent_needs_attention > 0 && (
                <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-rose-800">
                  urgent <b>{rep.urgent_needs_attention}</b>
                </span>
              )}
            </div>
          </Card>
        </div>

        {/* Second row — SI, Growth Operator, Agent activity */}
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <Card className="border border-slate-200 bg-white p-5" data-testid="dashboard-tile-si">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <Radar className="h-4 w-4" /> Search Intelligence
              </div>
              <Link to="/si" className="text-xs font-semibold text-orange-600 underline">Open</Link>
            </div>
            {si ? (
              <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                <MiniScore label="Ready" v={si.readiness} />
                <MiniScore label="Tech" v={si.technical_health} />
                <MiniScore label="Opp" v={si.search_opportunity} />
                <MiniScore label="Biz" v={si.business_alignment} />
              </div>
            ) : (
              <div className="mt-3 text-sm text-slate-500">No SI site connected yet. <Link to="/si" className="font-semibold text-orange-600 underline">Set one up</Link>.</div>
            )}
          </Card>

          <Card className="border border-slate-200 bg-white p-5" data-testid="dashboard-tile-go">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <Sparkles className="h-4 w-4" /> Growth Operator
              </div>
              <Link to="/growth-operator/hub" className="text-xs font-semibold text-orange-600 underline">Open hub</Link>
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-heading text-5xl font-black tabular-nums text-slate-900">{go.open_tasks ?? 0}</span>
              <span className="text-sm text-slate-500">open tasks</span>
            </div>
            <div className="mt-2 text-xs text-slate-500">
              Draft / awaiting approval / in progress across every industry pack.
            </div>
          </Card>

          <Card className="border border-slate-200 bg-white p-5" data-testid="dashboard-tile-mm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <Bot className="h-4 w-4" /> AI Marketing Manager
              </div>
              <Link to="/marketing-manager" className="text-xs font-semibold text-orange-600 underline">Open</Link>
            </div>
            <div className="mt-3 text-sm text-slate-600">
              This week's prioritised action list from every live signal above.
            </div>
            <Button asChild size="sm" className="mt-3 bg-slate-900 text-white hover:bg-slate-800">
              <Link to="/marketing-manager">See priorities <ArrowRight className="ml-1 h-3 w-3" /></Link>
            </Button>
          </Card>
        </div>

        {/* Bottom row — quick actions */}
        <div className="mt-8 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-sky-50 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-700">
                <TrendingUp className="h-4 w-4" /> Quick actions
              </div>
              <h2 className="mt-1 font-heading text-xl font-bold text-slate-900">
                Push the needle in 5 minutes
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline"><Link to="/local-visibility">Run LV audit</Link></Button>
              <Button asChild variant="outline"><Link to="/reputation">Send review request</Link></Button>
              <Button asChild variant="outline"><Link to="/inbox">Reply to leads</Link></Button>
              <Button asChild className="bg-orange-600 text-white hover:bg-orange-700"><Link to="/marketing-manager">See priorities</Link></Button>
            </div>
          </div>
        </div>

        {/* Weekly digest — customer-manageable PDF email */}
        <WeeklyDigestCard />

        <div className="mt-6 flex flex-wrap gap-2">
          <Link to="/dashboard/locations"
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="locations-link">
            View multi-location rollup →
          </Link>
        </div>

        <div className="mt-6 text-xs text-slate-400">
          Data generated {new Date(data.generated_at).toLocaleString()} · Every score explained on the
          {" "}<Link to="/si/methodology" className="underline">methodology page</Link>.
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MiniScore({ label, v }) {
  const tone = scoreTone(v);
  return (
    <div className={`rounded-lg border p-2 ${TONE[tone]}`}>
      <div className="text-[9px] font-semibold uppercase opacity-70">{label}</div>
      <div className="font-heading text-2xl font-black tabular-nums">{v ?? "—"}</div>
    </div>
  );
}
